import React, { useEffect, useState } from "react";

import {
  Users,
  BadgeIndianRupee,
  Calculator,
  TrendingUp,
  Shield,
  Crown,
  Lock,
  HeartHandshake,
  MessageSquare,
  Send,
  CheckCircle2,
} from "lucide-react";

import DashboardLayout from "../components/layout/DashboardLayout";

import { api, fmtINR } from "../lib/api";

export default function Admin() {
  const [stats, setStats] = useState(null);

  const [users, setUsers] = useState([]);

  const [usage, setUsage] = useState([]);

  const [recentRuns, setRecentRuns] = useState([]);

  const [loading, setLoading] = useState(true);

  const [tickets, setTickets] = useState([]);

  const [replyMap, setReplyMap] = useState({});

  const [sendingReply, setSendingReply] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, u, c, t] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/calculator-usage"),
          api.get("/admin/support/tickets"),
        ]);

        setStats(s.data);

        setUsers(u.data.users || []);
        setTickets(t.data.tickets || []);
        if (t.data.tickets?.length > 0) {
          setSelectedTicket(t.data.tickets[0]);
        }

        setUsage(c.data.usage || []);

        setRecentRuns(c.data.recent_runs || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sendReply = async (ticketId) => {
    const message = replyMap[ticketId];

    if (!message?.trim()) return;

    try {
      setSendingReply(true);

      await api.post(`/admin/support/tickets/${ticketId}/reply`, {
        message,
      });

      const refreshed = await api.get("/admin/support/tickets");

      const updatedTickets = refreshed.data.tickets || [];

      setTickets(updatedTickets);

      // ============================================
      // UPDATE CURRENTLY OPEN TICKET
      // ============================================

      const updatedSelected = updatedTickets.find((t) => t.id === ticketId);

      if (updatedSelected) {
        setSelectedTicket(updatedSelected);
      }

      // ============================================
      // CLEAR REPLY BOX
      // ============================================

      setReplyMap((prev) => ({
        ...prev,
        [ticketId]: "",
      }));
    } finally {
      setSendingReply(false);
    }
  };

  const resolveTicket = async (ticketId) => {
    try {
      await api.post(`/admin/support/tickets/${ticketId}/status`, {
        status: "resolved",
      });

      const refreshed = await api.get("/admin/support/tickets");

      const updatedTickets = refreshed.data.tickets || [];

      setTickets(updatedTickets);

      // ============================================
      // AUTO SELECT NEXT TICKET
      // ============================================

      if (updatedTickets.length > 0) {
        setSelectedTicket(updatedTickets[0]);
      } else {
        setSelectedTicket(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-24 text-center text-sm text-slate-500">
          Loading admin console…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">
          Admin Console
        </p>

        <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
          AccessWealth Operations
        </h1>

        <p className="mt-2 text-sm text-slate-500">
          Product engagement, onboarding health, premium interest and platform
          activity.
        </p>
      </div>

      {/* ================================================= */}
      {/* TOP KPIs */}
      {/* ================================================= */}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon={Users}
          label="Verified Users"
          value={stats.users.verified}
        />

        <Stat
          icon={BadgeIndianRupee}
          label="Projected MRR"
          value={fmtINR(stats.projected_revenue.projected_mrr)}
          accent="text-emerald-600"
        />

        <Stat
          icon={TrendingUp}
          label="Projected ARR"
          value={fmtINR(stats.projected_revenue.projected_arr)}
          accent="text-emerald-600"
        />

        <Stat
          icon={Calculator}
          label="Calculator Runs"
          value={stats.calculations.total_runs}
        />
      </section>

      {/* ================================================= */}
      {/* USER GROWTH + PREMIUM INTEREST */}
      {/* ================================================= */}

      <section className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card title="User Growth">
          <div className="grid grid-cols-3 gap-3">
            <MiniStat icon={Users} label="Today" value={stats.users.today} />

            <MiniStat
              icon={TrendingUp}
              label="This Week"
              value={stats.users.this_week}
            />

            <MiniStat
              icon={Crown}
              label="This Month"
              value={stats.users.this_month}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-5">
            <PlanCard
              label="Verified"
              count={stats.users.verified}
              total={stats.users.total}
              color="bg-emerald-500"
            />

            <PlanCard
              label="Pending Verification"
              count={stats.users.unverified}
              total={stats.users.total}
              color="bg-red-500"
            />
          </div>
        </Card>

        <Card title="Premium Interest Funnel">
          {/* ================================================= */}
          {/* REGISTERED USERS */}
          {/* ================================================= */}

          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Registered Users
              </div>

              <Badge tone="emerald">
                {stats.subscription_interest.total_interest} interests
              </Badge>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <MiniStat
                icon={Crown}
                label="Monthly"
                value={stats.subscription_interest.premium_monthly}
              />

              <MiniStat
                icon={BadgeIndianRupee}
                label="Yearly"
                value={stats.subscription_interest.premium_yearly}
              />

              <MiniStat
                icon={HeartHandshake}
                label="Family"
                value={stats.subscription_interest.family_yearly}
              />
            </div>

            <div className="mt-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Verified User Conversion
              </div>

              <div className="mt-2 text-4xl font-bold text-emerald-600 num-mono">
                {stats.subscription_interest.conversion_interest_percent}%
              </div>

              <div className="mt-2 text-xs text-slate-500">
                Verified users who registered premium interest.
              </div>
            </div>
          </div>

          {/* ================================================= */}
          {/* NON REGISTERED LEADS */}
          {/* ================================================= */}

          <div className="mt-8 border-t border-slate-200 dark:border-slate-800 pt-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Non-Registered Leads
              </div>

              <Badge tone="amber">
                {stats.non_registered_interest.total_interest} leads
              </Badge>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <MiniStat
                icon={Crown}
                label="Monthly"
                value={stats.non_registered_interest.premium_monthly}
              />

              <MiniStat
                icon={BadgeIndianRupee}
                label="Yearly"
                value={stats.non_registered_interest.premium_yearly}
              />

              <MiniStat
                icon={HeartHandshake}
                label="Family"
                value={stats.non_registered_interest.family_yearly}
              />
            </div>

            <div className="mt-5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 p-5">
              <div className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-300">
                External Premium Demand
              </div>

              <div className="mt-2 text-4xl font-bold text-amber-600 num-mono">
                {stats.non_registered_interest.unique_emails}
              </div>

              <div className="mt-2 text-xs text-amber-700/80 dark:text-amber-300/80">
                Unique emails interested before account creation.
              </div>
            </div>
          </div>

          {/* ================================================= */}
          {/* TOTAL PIPELINE */}
          {/* ================================================= */}

          <div className="mt-8 rounded-3xl border border-emerald-200 dark:border-emerald-500/20 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-slate-900 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  Total Subscription Pipeline
                </div>

                <div className="mt-2 text-5xl font-bold text-emerald-600 num-mono">
                  {stats.subscription_interest.total_interest +
                    stats.non_registered_interest.total_interest}
                </div>
              </div>

              <div className="grid h-16 w-16 place-items-center rounded-3xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600">
                <TrendingUp className="h-8 w-8" />
              </div>
            </div>

            <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">
              Combined registered and external premium interest pipeline before
              launch.
            </div>
          </div>
        </Card>
      </section>

      {/* ================================================= */}
      {/* FEATURE ADOPTION */}
      {/* ================================================= */}

      <section className="grid lg:grid-cols-3 gap-6 mt-6">
        <Card title="Goals Adoption">
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Goal Adoption
            </div>

            <div className="mt-2 text-4xl font-bold text-blue-600 num-mono">
              {stats.goals.adoption_percent}%
            </div>

            <div className="mt-2 text-xs text-slate-500">
              Verified users actively using goals.
            </div>

            <div className="mt-5 space-y-3">
              <Row label="Total Goals" value={stats.goals.total} />

              <Row label="Completed Goals" value={stats.goals.completed} />

              <Row
                label="Users With Goals"
                value={stats.goals.users_with_goals}
              />
            </div>
          </div>
        </Card>

        <Card title="Nominee Adoption">
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Nominee Adoption
            </div>

            <div className="mt-2 text-4xl font-bold text-emerald-600 num-mono">
              {stats.nominees.adoption_percent}%
            </div>

            <div className="mt-2 text-xs text-slate-500">
              Users who added nominees.
            </div>

            <div className="mt-5 space-y-3">
              <Row label="Total Nominees" value={stats.nominees.total} />

              <Row
                label="Users With Nominees"
                value={stats.nominees.users_with_nominees}
              />
            </div>
          </div>
        </Card>

        <Card title="Family Vault">
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Vault Adoption
            </div>

            <div className="mt-2 text-4xl font-bold text-purple-600 num-mono">
              {stats.family_vault.adoption_percent}%
            </div>

            <div className="mt-2 text-xs text-slate-500">
              Users using family vault features.
            </div>

            <div className="mt-5 space-y-3">
              <Row
                label="Family Members"
                value={stats.family_vault.family_members}
              />

              <Row
                label="Inheritance Allocations"
                value={stats.family_vault.inheritance_allocations}
              />

              <Row
                label="Families Using Vault"
                value={stats.family_vault.families_using_vault}
              />
            </div>
          </div>
        </Card>
      </section>

      {/* ================================================= */}
      {/* SECURITY + PLATFORM HEALTH */}
      {/* ================================================= */}

      <section className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card title="Security">
          <div className="grid grid-cols-2 gap-3">
            <MiniStat
              icon={Lock}
              label="Locked Accounts"
              value={stats.security.locked_accounts}
            />

            <MiniStat
              icon={Shield}
              label="Failed Logins"
              value={stats.security.failed_login_records}
            />
          </div>
        </Card>

        <Card title="Platform Health">
          <div className="space-y-4">
            <HealthRow
              label="Goal Adoption"
              value={stats.platform_health.goal_adoption_percent}
            />

            <HealthRow
              label="Nominee Adoption"
              value={stats.platform_health.nominee_adoption_percent}
            />

            <HealthRow
              label="Family Vault Adoption"
              value={stats.platform_health.family_vault_adoption_percent}
            />

            <HealthRow
              label="Calculator Engagement"
              value={stats.platform_health.calculator_engagement_percent}
            />
          </div>
        </Card>
      </section>

      {/* ================================================= */}
      {/* CALCULATORS */}
      {/* ================================================= */}

      <section className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card title="Top Calculators">
          <div className="space-y-3">
            {usage.map((u) => (
              <div
                key={u.slug}
                className="flex items-center justify-between rounded-xl border border-slate-100 dark:border-slate-800 p-3"
              >
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {u.slug}
                  </div>

                  <div className="text-xs text-slate-500">
                    {u.unique_users} unique users
                  </div>
                </div>

                <div className="text-lg font-bold text-emerald-600 num-mono">
                  {u.count}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Recent Calculator Activity">
          <div className="space-y-3">
            {recentRuns.map((r, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-100 dark:border-slate-800 p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {r.slug}
                  </div>

                  <div className="text-xs text-slate-500">
                    {r.created_at?.slice(0, 10)}
                  </div>
                </div>

                <div className="mt-1 text-xs text-slate-500">
                  {r.label || "Untitled calculation"}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* ================================================= */}
      {/* SUPPORT TICKETS */}
      {/* ================================================= */}

      {/* ================================================= */}
      {/* SUPPORT CENTER */}
      {/* ================================================= */}

      <section className="card-soft mt-6 overflow-hidden">
        <div className="grid lg:grid-cols-[380px_1fr] min-h-[850px]">
          {/* ============================================= */}
          {/* LEFT SIDEBAR */}
          {/* ============================================= */}

          <div className="border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                    Support Inbox
                  </h3>

                  <p className="text-xs text-slate-500 mt-1">
                    Active customer conversations
                  </p>
                </div>

                <div className="rounded-full bg-blue-100 dark:bg-blue-500/10 px-3 py-1 text-xs font-bold text-blue-700 dark:text-blue-300">
                  {tickets.length}
                </div>
              </div>
            </div>

            <div className="overflow-y-auto h-[780px]">
              {tickets.map((ticket) => {
                const active = selectedTicket?.id === ticket.id;

                return (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left p-5 border-b border-slate-200 dark:border-slate-800 transition-all ${
                      active
                        ? "bg-white dark:bg-slate-950"
                        : "hover:bg-white/70 dark:hover:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-white truncate">
                          {ticket.subject}
                        </div>

                        <div className="mt-1 text-xs text-slate-500 truncate">
                          {ticket.user_name}
                        </div>
                      </div>

                      <Badge
                        tone={
                          ticket.status === "resolved"
                            ? "emerald"
                            : ticket.status === "waiting_user"
                              ? "amber"
                              : "blue"
                        }
                      >
                        {ticket.status}
                      </Badge>
                    </div>

                    <div className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                      {ticket.last_message || "No messages"}
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-[11px] text-slate-400">
                        {ticket.ticket_number}
                      </div>

                      <div className="text-[11px] text-slate-400">
                        {ticket.last_message_at?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ============================================= */}
          {/* RIGHT CHAT PANEL */}
          {/* ============================================= */}

          <div className="flex flex-col bg-white dark:bg-slate-950">
            {!selectedTicket ? (
              <div className="flex-1 grid place-items-center">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-slate-300" />

                  <div className="mt-4 text-lg font-semibold text-slate-700 dark:text-slate-200">
                    Select a ticket
                  </div>

                  <div className="mt-1 text-sm text-slate-500">
                    Choose a conversation from the left
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* HEADER */}

                <div className="border-b border-slate-200 dark:border-slate-800 px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xl font-bold text-slate-900 dark:text-white">
                        {selectedTicket.subject}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        <span>{selectedTicket.user_name}</span>

                        <span>•</span>

                        <span>{selectedTicket.user_email}</span>

                        <span>•</span>

                        <span>{selectedTicket.ticket_number}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge tone="blue">{selectedTicket.category}</Badge>

                      <Badge tone="emerald">{selectedTicket.status}</Badge>
                    </div>
                  </div>
                </div>

                {/* CHAT */}

                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                  {selectedTicket.messages?.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender_type === "admin"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-3xl rounded-3xl px-5 py-4 ${
                          msg.sender_type === "admin"
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white"
                        }`}
                      >
                        <div className="text-xs uppercase tracking-widest opacity-70 mb-2">
                          {msg.sender_type}
                        </div>

                        <div className="text-sm whitespace-pre-wrap leading-relaxed">
                          {msg.message}
                        </div>

                        <div
                          className={`mt-3 text-[10px] ${
                            msg.sender_type === "admin"
                              ? "text-emerald-100"
                              : "text-slate-500"
                          }`}
                        >
                          {msg.created_at?.slice(0, 16).replace("T", " ")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* REPLY BAR */}

                <div className="border-t border-slate-200 dark:border-slate-800 p-5 bg-slate-50 dark:bg-slate-900/50">
                  <div className="flex gap-4">
                    <textarea
                      rows={4}
                      value={replyMap[selectedTicket.id] || ""}
                      onChange={(e) =>
                        setReplyMap((prev) => ({
                          ...prev,
                          [selectedTicket.id]: e.target.value,
                        }))
                      }
                      placeholder="Reply to customer..."
                      className="flex-1 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-5 py-4 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />

                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => sendReply(selectedTicket.id)}
                        disabled={sendingReply}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        <Send className="h-4 w-4" />
                        Reply
                      </button>

                      <button
                        onClick={() => resolveTicket(selectedTicket.id)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Resolve
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* USERS TABLE */}
      {/* ================================================= */}

      <section className="card-soft p-6 mt-6">
        <div className="mb-5">
          <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">
            All Users
          </h3>

          <p className="text-xs text-slate-500 mt-1">
            Verified onboarding and platform membership overview.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Verified</th>
                <th className="px-4 py-3 text-left">Joined</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100 dark:border-slate-800/60"
                >
                  <td className="px-4 py-4 font-semibold text-slate-900 dark:text-white">
                    {u.name}
                  </td>

                  <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-300">
                    {u.email}
                  </td>

                  <td className="px-4 py-4">
                    <Badge tone={u.role === "admin" ? "amber" : "slate"}>
                      {u.role}
                    </Badge>
                  </td>

                  <td className="px-4 py-4">
                    <Badge tone={u.plan === "premium" ? "emerald" : "slate"}>
                      {u.plan}
                    </Badge>
                  </td>

                  <td className="px-4 py-4">
                    <Badge tone={u.email_verified ? "emerald" : "red"}>
                      {u.email_verified ? "verified" : "pending"}
                    </Badge>
                  </td>

                  <td className="px-4 py-4 text-xs text-slate-500">
                    {u.created_at?.slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </DashboardLayout>
  );
}

function Card({ title, children }) {
  return (
    <div className="card-soft p-6">
      <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
        {title}
      </h3>

      <div className="mt-5">{children}</div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  accent = "text-slate-900 dark:text-white",
}) {
  return (
    <div className="card-soft p-5">
      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-amber-50 dark:bg-amber-500/10 text-amber-600">
        <Icon className="h-5 w-5" />
      </div>

      <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">
        {label}
      </div>

      <div className={`mt-1 text-3xl font-bold num-mono ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          {label}
        </div>

        <Icon className="h-4 w-4 text-slate-400" />
      </div>

      <div className="mt-2 text-2xl font-bold num-mono text-slate-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

function PlanCard({ label, count, total, color }) {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;

  return (
    <div className="rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
      <div className="text-xs uppercase tracking-widest text-slate-500">
        {label}
      </div>

      <div className="mt-2 text-3xl font-bold num-mono text-slate-900 dark:text-white">
        {count}
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-2 text-xs text-slate-500">{pct}% of users</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
      <div className="text-sm text-slate-600 dark:text-slate-300">{label}</div>

      <div className="font-bold num-mono text-slate-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

function HealthRow({ label, value }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {label}
        </div>

        <div className="text-sm font-bold text-emerald-600 num-mono">
          {value}%
        </div>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className="h-full bg-emerald-500"
          style={{
            width: `${value}%`,
          }}
        />
      </div>
    </div>
  );
}

function Badge({ tone = "slate", children }) {
  const map = {
    amber:
      "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",

    emerald:
      "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",

    red: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300",

    slate: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
  };

  return (
    <span
      className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-widest font-semibold ${map[tone]}`}
    >
      {children}
    </span>
  );
}
